import {Section} from 'constants/NavConstants';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import {getFileSummaryQueryParams} from 'search/GlobalSearchDAO';
import * as logging from 'util/Logging';
import * as net from 'util/Net';

import {
  FilterErrors,
  GLOBAL_SEARCH_URL,
  SearchParams,
  SearchResponse,
  SearchResults,
  SearchStatus,
  SearchType,
} from './SearchConstants';
import {sanitizeSearchParams} from './SearchUtil';

const NON_ARCHIVABLE_SEARCH_TYPES = [SearchType.WORKLIST];

// These search types shouldn't automatically sort starred items to the top of the listing.
export const IGNORE_STARRING_SEARCH_TYPES: Array<SearchType> = [SearchType.GLOBAL];

export const START_POLLING_INTERVAL: number = 500;
export const BACKOFF_THRESHOLD: number = 6;
export const BACKOFF_INCREMENT: number = 500;
export const MAX_POLLING_INTERVAL: number = 5000;
// Timeout that the UI should wait for the async BLAST worker to return results to the monolith. Note that
// most search timeouts (e.g. the BLAST worker hits BLAST_SUBPROCESS_TIMEOUT_SECONDS) and other errors should
// be caught in the worker and sent to the monolith as an error which will be pollable. This timeout is only
// relevant when the monolith never hears back from the worker at all before the deadline, and is intended to
// prevent the UI from hanging indefinitely if there is a problem with async BLAST search.
// The value should always be set to at least BLAST_SUBPROCESS_TIMEOUT_SECONDS plus some additional buffer
// time to account for time that the request in the SQS queue, potential retries from transient issues, etc.
export const ASYNC_BLAST_POLLING_TIMEOUT_MS: number = 1000 * 60 * 15; // 15 minutes

export function performSearch(
  type: SearchType,
  searchParams: SearchParams,
  filterErrors: FilterErrors,
  requestConsistency?: boolean
): Promise<SearchResponse> {
  // sanitizeSearchParams adds a filter to only show non-archived items
  // by default. However, some objects (like Worklists) cannot be archived and
  // don't have a concept of archiving, so we must leave that filter off in those cases.
  const includeArchiveFilter = !NON_ARCHIVABLE_SEARCH_TYPES.includes(type);
  const sanitizedSearchParams = sanitizeSearchParams(searchParams, filterErrors, {
    includeArchiveFilter,
    includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter: true,
    includeProcessesIsSystemDataFilter: true,
  });
  return GlobalSearchActions.listItems(sanitizedSearchParams, type, requestConsistency);
}

export async function pollSearchStatus(searchId: string, type: SearchType): Promise<SearchResults> {
  const serializationOptions = GlobalSearchActions.getFileSummarySerializationOptions(type);
  const queryParams = getFileSummaryQueryParams(
    serializationOptions.excludeTags,
    serializationOptions.includeOnlyRegistryAliases
  );
  const url = `${GLOBAL_SEARCH_URL}/poll${queryParams.length > 0 ? `?${queryParams.join('&')}` : ''}`;
  const startTime = Date.now();
  let result = await net.postJSONPromise({url, data: {searchId}});
  let attempt = 1;
  while (result.status === SearchStatus.IN_PROGRESS) {
    if (Date.now() - startTime >= ASYNC_BLAST_POLLING_TIMEOUT_MS) {
      logging.error('Async BLAST search polling timed out.', {
        extra: {searchId, attempt, ASYNC_BLAST_POLLING_TIMEOUT_MS},
      });
      // Return a timed out result which allows the UI to stop loading and display a user-friendly message.
      return {
        searchId,
        status: SearchStatus.COMPLETE,
        ids: [],
        groupItemIds: [],
        total: 0,
        isExact: false,
        timedOut: true,
      };
    }

    const multiplier = Math.max(attempt - BACKOFF_THRESHOLD, 0);
    const duration = Math.min(START_POLLING_INTERVAL + BACKOFF_INCREMENT * multiplier, MAX_POLLING_INTERVAL);

    // eslint-disable-next-line no-promise-executor-return
    await new Promise((resolve) => setTimeout(resolve, duration));

    result = await net.postJSONPromise({url, data: {searchId}});

    attempt++;
  }

  return GlobalSearchActions.globalSearchResponseToSearchResults(result, {
    hasPartialFileSummaries: GlobalSearchActions.hasPartialFileSummaries(serializationOptions),
  });
}

export const SEARCH_TYPE_BY_SECTION: {[section: string]: SearchType} = {
  [Section.ANALYTICS]: SearchType.ANALYSES_AND_ANALYTICS_DASHBOARDS,
  [Section.BENCHLING_APPS]: SearchType.BENCHLING_APPS,
  [Section.CONNECTIONS]: SearchType.CONNECTIONS,
  [Section.JELLYFISH]: SearchType.JELLYFISH,
  [Section.REGISTRIES]: SearchType.BIOENTITIES,
  [Section.PROJECT_ITEMS]: SearchType.FOLDERS_AND_ITEMS,
  [Section.PROJECTS]: SearchType.PROJECTS,
  [Section.PROCESSES]: SearchType.PROCESS_MANAGEMENT,
  [Section.REQUESTS]: SearchType.REQUEST,
  [Section.REQUESTS_V2_SUBMISSIONS]: SearchType.REQUEST_V2_DASHBOARD,
  [Section.REQUESTS_V2_DEFINITIONS]: SearchType.REQUEST_V2_TYPES,
  [Section.SEARCH]: SearchType.GLOBAL,
  [Section.STORAGE]: SearchType.STORABLE,
  [Section.STUDIES]: SearchType.STUDIES,
  [Section.STUDY_ITEMS]: SearchType.STUDY_ITEMS,
  [Section.TEMPLATE_COLLECTIONS]: SearchType.TEMPLATE_COLLECTION,
  [Section.WORKFLOWS]: SearchType.WORKFLOW,
  [Section.WORKLISTS]: SearchType.WORKLIST,
  [Section.WORKLIST_ITEMS]: SearchType.WORKLIST_ITEMS,
};
